<script setup lang="ts">
import { useStore } from '@/stores/store'
import { storeToRefs } from 'pinia'
import MaterialPopup from '@/components/Materials/MaterialPopup.vue'
import { usePopup } from '@/stores/popup'
import { IconArrowLeft, IconArrowRight, IconReload, IconInfo } from '@/components/icons'
import { computed } from 'vue'
import { ref } from 'vue'
import { Dialog, DialogTrigger } from '@/components/ui/dialog'
const store = useStore()
const popups = usePopup()

const { fabrics, cushionBottom, currentStep } = storeToRefs(store)
const isTipVisible = ref(false)
const isFabricsFilled = computed(() => {
  return Boolean(fabrics.value?.data?.name)
})
const isCushionBottomFilled = computed(() => {
  return cushionBottom.value.length > 0
})

function toBack() {
  currentStep.value = 1
  document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
}
function toNext() {
  currentStep.value = 3
  document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <div class="flex flex-col space-y-6 lg:space-y-10" v-if="currentStep === 2">
    <div class="flex justify-between items-start">
      <fieldset class="inline-block">
        <legend class="text-xl lg:text-2xl font-semibold mb-6">
          {{ $t('Material on top, sides & possibly buttons') }}
        </legend>
        <Dialog v-model:open="popups.materialPopup">
          <DialogTrigger as-child v-if="!isFabricsFilled">
            <button
              v-if="!isFabricsFilled"
              type="button"
              class="btn justify-between border border-black px-6 py-4 w-full max-w-[360px]"
            >
              <span class="font-normal">{{ $t('Choose a color') }}</span>
              <span class="underline">{{ $t('Select') }}</span>
            </button>
          </DialogTrigger>
          <DialogTrigger as-child v-if="isFabricsFilled">
            <button
              v-if="isFabricsFilled"
              type="button"
              class="btn flex-row items-center justify-between border border-black px-6 py-4 w-full max-w-[360px]"
            >
              <div class="flex gap-2 items-center">
                <div v-if="fabrics?.data?.image" class="option-image">
                  <img
                    :src="fabrics?.data?.image"
                    :alt="fabrics?.data?.name ? fabrics?.data?.name : 'Fabric'"
                    class="w-4 rounded-full"
                  />
                </div>
                <span v-if="fabrics?.data?.name" class="option-name">
                  {{ fabrics?.data?.name }}
                </span>
              </div>
              <span class="underline">{{ $t('Change') }}</span>
            </button>
          </DialogTrigger>
          <MaterialPopup />
        </Dialog>
      </fieldset>
      <div class="hidden lg:flex justify-center items-center" v-if="fabrics.data.image">
        <img
          class="object-cover w-full max-w-[106px]"
          :src="fabrics.data.image"
          :alt="fabrics.data.name"
        />
      </div>
    </div>
    <fieldset>
      <div class="inline-flex justify-start items-center w-full relative gap-2">
        <legend class="text-xl lg:text-2xl font-semibold">{{ $t('Material on the underside') }}</legend>
        <div class="flex">
          <button class="text-black/40" @click="isTipVisible = !isTipVisible">
            <IconInfo />
          </button>
          <transition>
            <div
              class="absolute z-10 top-8 left-0 bg-black/80 px-5 lg:px-6  py-4 text-white w-screen ml-[50%] -translate-x-1/2 max-w-[480px] space-y-1"
              v-if="isTipVisible"
            >
              <p class="font-semibold">{{ $t('Same material as top side') }}</p>
              <p>
                {{
                  $t(
                    'If you choose this, your cushion will have the same material on the underside as the one you have chosen on the top.',
                  )
                }}
              </p>
              <p class="font-semibold">{{ $t('Anti-slip') }}</p>
              <p>
                {{
                  $t(
                    'Anti-slip is a material that ensures that your cushion stays on your furniture.',
                  )
                }}
              </p>
            </div>
          </transition>
        </div>
      </div>

      <div class="shape-wrapper flex-nowrap mt-[18px] gap-4 mt-6">
        <label class="shape-item basis-auto">
          <div class="shape-wrapper-inner" v-if="fabrics.data.image">
            <img
              class="object-cover w-[168px] h-[137px]"
              :src="fabrics.data.image"
              :alt="fabrics.data.name"
            />
          </div>
          <div
            class="w-[168px] h-[137px] border border-black/20 text-black/60 flex justify-center items-center"
            v-if="!fabrics.data.image"
          >
            <span>{{ $t('Fill in the details') }}</span>
          </div>
          <div class="flex items-center gap-2 mt-4">
            <input
              id="reversible_cushion_bottom_type"
              v-model="cushionBottom"
              type="radio"
              value="reversible_cushion_bottom_type"
              name="cushionBottom"
              class="radio-input"
            />
            <span class="shape-label">{{ $t('As top side') }}</span>
          </div>
        </label>
        <label class="shape-item basis-auto">
          <div class="shape-wrapper-inner">
            <img
              class="object-cover w-[168px] h-[137px]"
              :src="store.imgPath + '/form/anti-slip.jpg'"
              :alt="$t('Anti-slip')"
            />
          </div>
          <div class="inline-flex items-center gap-2 mt-4">
            <input
              id="anti_slip_cushion_bottom_type"
              v-model="cushionBottom"
              type="radio"
              value="anti_slip_cushion_bottom_type"
              name="cushionBottom"
              class="radio-input"
            />
            <span class="shape-label">{{ $t('Anti-slip') }}</span>
          </div>
        </label>
      </div>
    </fieldset>
    <span class="text-black/60">{{ $t('*The images are indicative') }}</span>

    <div class="flex flex-col lg:flex-row gap-4 justify-between">
      <div class="flex gap-2">
        <button type="button" class="btn btn-primary" @click="toBack">
          <IconArrowLeft />
          <span>{{ $t('Back') }}</span>
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!(isFabricsFilled && isCushionBottomFilled)"
          @click="toNext"
        >
          <span>{{ $t('Next') }}</span>
          <IconArrowRight />
        </button>
      </div>
      <button type="button" class="btn" @click="store.nullMaterial">
        <IconReload />
        <span>{{ $t('Drop off') }}</span>
      </button>
    </div>
  </div>
</template>
